import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Store, LayoutDashboard, Heart, Package, LogOut, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

function SearchBar() {
  const [q, setQ] = useState("");
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setSugs([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/autocomplete", { params: { q } });
        setSugs(data.suggestions);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const submit = (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="relative flex-1 max-w-2xl">
      <form onSubmit={submit} className="flex">
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder='Try "black running shoes under ₹5000"'
          className="w-full h-11 rounded-l-full border border-r-0 border-slate-200 bg-slate-50 px-5 text-sm outline-none focus:border-blue-500 focus:bg-white transition-colors"
        />
        <button
          data-testid="search-submit-btn"
          type="submit"
          className="h-11 px-5 rounded-r-full bg-slate-900 text-white hover:bg-slate-700 transition-colors"
          aria-label="Search"
        >
          <Search className="w-4 h-4" />
        </button>
      </form>
      {open && sugs.length > 0 && (
        <div data-testid="search-autocomplete" className="absolute z-50 mt-2 w-full rounded-xl border border-slate-100 bg-white shadow-2xl overflow-hidden">
          {sugs.map((s, i) => (
            <button
              key={`${s.type}-${s.text}-${i}`}
              data-testid={`search-suggestion-${i}`}
              onMouseDown={() => {
                setOpen(false);
                if (s.type === "product") nav(`/product/${s.id}`);
                else nav(`/search?q=${encodeURIComponent(s.text)}`);
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors"
            >
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <span className="truncate">{s.text}</span>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-400">{s.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4 sm:gap-8">
          <Link to="/" data-testid="logo-link" className="font-heading text-2xl font-800 tracking-tight text-slate-900 shrink-0" style={{ fontWeight: 800 }}>
            Hello Nanba<span className="text-blue-500">.</span>
          </Link>
          <SearchBar />
          <nav className="flex items-center gap-1 sm:gap-2 shrink-0">
            {!user?.seller && (
              <Button asChild variant="ghost" className="hidden md:flex text-sm">
                <Link to="/sell" data-testid="become-seller-link"><Store className="w-4 h-4 mr-1.5" />Sell</Link>
              </Button>
            )}
            <Link to="/cart" data-testid="cart-link" className="relative p-2.5 rounded-full hover:bg-slate-100 transition-colors">
              <ShoppingCart className="w-5 h-5 text-slate-700" />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="account-menu-btn" className="flex items-center gap-2 p-2 rounded-full hover:bg-slate-100 transition-colors">
                    <span className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase()}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/orders")}><Package className="w-4 h-4 mr-2" />Orders</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-wishlist" onClick={() => nav("/wishlist")}><Heart className="w-4 h-4 mr-2" />Wishlist</DropdownMenuItem>
                  {user.role === "seller" && (
                    <DropdownMenuItem data-testid="menu-seller-portal" onClick={() => nav("/seller")}><Store className="w-4 h-4 mr-2" />Seller Portal</DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><ShieldCheck className="w-4 h-4 mr-2" />Admin Centre</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-700">
                <Link to="/login" data-testid="signin-link"><User className="w-4 h-4 mr-1.5" />Sign in</Link>
              </Button>
            )}
          </nav>
        </div>
        {categories.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-2 flex gap-1 overflow-x-auto">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/search?category=${encodeURIComponent(c.name)}`}
                data-testid={`nav-category-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-xs font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-full whitespace-nowrap transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-slate-100 bg-slate-50 mt-20">
        <div className="max-w-7xl mx-auto px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <p className="font-heading font-bold text-lg mb-3">Hello Nanba<span className="text-blue-500">.</span></p>
            <p className="text-slate-500 text-xs leading-relaxed">A multi-vendor marketplace where independent sellers offer the products you love.</p>
          </div>
          <div>
            <p className="font-semibold mb-3 text-xs uppercase tracking-widest text-slate-400">Shop</p>
            <div className="space-y-2 text-slate-600">
              <Link to="/search" className="block hover:text-blue-600">All products</Link>
              <Link to="/search?sort=discount" className="block hover:text-blue-600">Deals</Link>
              <Link to="/blog" className="block hover:text-blue-600">Blog</Link>
            </div>
          </div>
          <div>
            <p className="font-semibold mb-3 text-xs uppercase tracking-widest text-slate-400">Sell</p>
            <div className="space-y-2 text-slate-600">
              <Link to="/sell" className="block hover:text-blue-600">Become a seller</Link>
              <Link to="/vendor/login" className="block hover:text-blue-600">Vendor login</Link>
              <Link to="/seller" className="block hover:text-blue-600">Seller portal</Link>
            </div>
          </div>
          <div>
            <p className="font-semibold mb-3 text-xs uppercase tracking-widest text-slate-400">Account</p>
            <div className="space-y-2 text-slate-600">
              <Link to="/orders" className="block hover:text-blue-600">Your orders</Link>
              <Link to="/wishlist" className="block hover:text-blue-600">Wishlist</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
